import React, { useState, useRef, memo, useCallback, useEffect } from 'react';
import { Box, Popper, MenuItem, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';

import RouteStatusEnum from '../../../../enums/RouteStatus';
import {
  isRouteStatusInBuild,
  isRouteStatusReady,
  isRouteStatusOpen,
} from '../../../../models/routeDetail';
import { LockedIcon } from '../../../icons';
import {
  LOCKED_BY,
  READY,
  OPEN,
  UNLOCK,
  LOCK,
  NO_DRIVER_ASSIGNED,
  SCHEDULE_REQUIRED,
} from '../../../../constants/strings';
import { View } from '../../../Base';

const useStyles = makeStyles({
  container: {
    cursor: 'pointer',
  },
  menuContainer: {
    background: 'white',
    paddingTop: '0.313rem',
    paddingBottom: '0.313rem',
    marginLeft: '-0.813rem',
    marginBottom: '1rem',
    borderRadius: '0.25rem',
    zIndex: 2,
  },
  selectItem: {
    paddingTop: '0.125rem',
    paddingBottom: '0.125rem',
    minWidth: '11.25rem',
    display: 'flex',
    fontSize: '0.875rem',
    justifyContent: 'space-between',
  },
  iconContainer: {
    width: '1rem',
    height: '1.5rem',
    paddingLeft: '0.625rem',
    alignItems: 'center',
    display: 'flex',
  },
});

// eslint-disable-next-line react/display-name
export const LoadTimeMenuItems = memo(({ items }) => {
  const styles = useStyles();
  return (
    <>
      {Object.entries(items).map(([label, value]) => (
        <MenuItem
          className={styles.selectItem}
          component="div"
          key={`${label} - ${value}`}
        >
          {`${label} - ${value}`}
        </MenuItem>
      ))}
    </>
  );
});

// eslint-disable-next-line react/display-name
export const StatusMenuItems = memo(
  ({
    routeLockedBy,
    routeStatus,
    setOpen,
    onItemClick,
    driverCode,
    scheduleRequired,
  }) => {
    const styles = useStyles();

    if (routeLockedBy) {
      return (
        <MenuItem
          className={styles.selectItem}
          component="div"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(false);
          }}
        >
          {`${LOCKED_BY} ${routeLockedBy}`}
          <View className={styles.iconContainer}>
            <LockedIcon />
          </View>
        </MenuItem>
      );
    }

    const items = [];
    const disableReadyStatus =
      isRouteStatusOpen({ status: routeStatus }) &&
      (!driverCode || scheduleRequired);
    const readyStatusTooltipTitle = disableReadyStatus
      ? !driverCode
        ? NO_DRIVER_ASSIGNED
        : SCHEDULE_REQUIRED
      : '';
    if (
      !isRouteStatusOpen({ status: routeStatus }) ||
      isRouteStatusInBuild({ status: routeStatus })
    ) {
      items.push(
        <MenuItem
          key={RouteStatusEnum.OPEN}
          className={styles.selectItem}
          component="div"
          onClick={(e) => onItemClick(e, RouteStatusEnum.OPEN)}
        >
          {OPEN}
        </MenuItem>
      );
    }
    if (
      !isRouteStatusReady({ status: routeStatus }) &&
      !isRouteStatusInBuild({ status: routeStatus })
    ) {
      items.push(
        <Tooltip title={readyStatusTooltipTitle} followCursor>
          <div>
            <MenuItem
              key={RouteStatusEnum.READY}
              className={styles.selectItem}
              disabled={disableReadyStatus}
              component="div"
              onClick={(e) => onItemClick(e, RouteStatusEnum.READY)}
            >
              {READY}
            </MenuItem>
          </div>
        </Tooltip>
      );
    }

    return items;
  }
);

// eslint-disable-next-line react/display-name
export const LockMenuItems = memo(({ routeLockedBy, onItemClick }) => {
  const styles = useStyles();
  const onLock = useCallback(
    (e) => onItemClick(e, !routeLockedBy),
    [onItemClick, routeLockedBy]
  );

  return (
    <MenuItem className={styles.selectItem} component="div" onClick={onLock}>
      {routeLockedBy ? UNLOCK : LOCK}
    </MenuItem>
  );
});

const ClickAwayListener = ({ children, onClickAway }) => {
  const nodeRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (nodeRef.current && !nodeRef.current.contains(event.target)) {
        onClickAway?.(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClickAway]);

  return <div ref={nodeRef}>{children}</div>;
};

const PopoverMenu = ({ onChange, render, icon, placement, onOpenPopover }) => {
  const styles = useStyles();
  const anchorEl = useRef(null);
  const [open, setOpen] = useState(false);

  const handleMenuItemClick = useCallback(
    (e, value) => {
      e.stopPropagation();
      setOpen(false);
      onChange(value);
    },
    [onChange]
  );

  const onClickAway = useCallback(() => {
    setOpen(false);
  }, []);

  const onClick = useCallback(
    (e) => {
      e.stopPropagation();
      setOpen(!open);

      onOpenPopover && onOpenPopover();
    },
    [open, onOpenPopover]
  );

  return (
    <Box ref={anchorEl} onClick={onClick} className={styles.container}>
      {icon}
      <Popper
        open={open}
        anchorEl={anchorEl && anchorEl.current}
        placement={placement}
        className={styles.menuContainer}
      >
        <ClickAwayListener onClickAway={onClickAway}>
          <div>{render({ handleMenuItemClick, setOpen })}</div>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default memo(PopoverMenu);
